//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import { DefaultButton } from '@components/atoms/button/Button';
import HR from '@components/atoms/hr/HR';
import { BodyText } from '@components/atoms/text/Text';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION } from '@components/media-filters/ProductTagsFilter';
import { SelectedOptions } from '@components/media-filters/SelectedOptions';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useProductDetailsQuery } from '@content-drawer/filters/labels/hooks/useProductsQuery';
import { getDisabledFilters } from '@content-drawer/selectors/getContentDrawerSettings';
import {
  getIsContentLibraryLabelsImprovementsEnabled,
  getIsContentLibraryProductsImprovementsEnabled,
} from '@media-library/redux/getMediaLibraryPage';
import { isDefined } from '@utils/filterPredicates';

const EXCLUDED_PARAM_KEYS: MediaFilterParamKey[] = ['userIds', 'isDistributed'];
const SUBSECTION_PARAM_KEYS: MediaFilterParamKey[] = ['labels', 'productIds'];

const getDistributionTitle = (isDistributed: boolean | undefined) =>
  isDistributed ? 'Distributed to' : 'Not distributed to';

export interface SelectedFiltersProps<T extends FilterValue> {
  sections: Map<MediaFilterParamKey, FilterSection<T>>;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  onReset: () => void;
}

export const SelectedFilters = <T extends FilterValue>({
  onChange,
  onReset,
  sections,
}: SelectedFiltersProps<T>) => {
  const disabledFilters = useSelector(getDisabledFilters);
  // TODO: PD-264130 - Remove content_library_labels_improvements feature flag
  const isContentLibraryLabelsImprovementsEnabled = useSelector(
    getIsContentLibraryLabelsImprovementsEnabled,
  );

  // TODO: PD-297467 - Remove content_library_products_improvements feature flag
  const isContentLibraryProductsImprovementsEnabled = useSelector(
    getIsContentLibraryProductsImprovementsEnabled,
  );

  const sectionsArray = Array.from(sections.values());
  const { filteredSections, filtersCount } = sectionsArray.reduce<{
    filtersCount: number;
    filteredSections: FilterSection<T>[];
  }>(
    (data, section) => {
      if (!EXCLUDED_PARAM_KEYS.includes(section.paramKey)) {
        const options = section.options.filter((option) => option.isSelected);
        data.filtersCount += options.length;
        if (options.length > 0) {
          data.filteredSections.push({
            ...section,
            options,
          });
        }
      }
      return data;
    },
    { filtersCount: 0, filteredSections: [] },
  );

  // remove an individual option from a section
  const onDeselect = (
    paramKey: MediaFilterParamKey,
    selected: FilterOption<T>,
    subsectionParamKey?: MediaFilterParamKey,
  ) => {
    const section = sections.get(paramKey);
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    if (isDefined(section)) {
      const options = section.options.map((option) => ({
        ...option,
        isSelected: option.value === selected.value ? false : option.isSelected,
      }));

      updatedSections.set(section.paramKey, { ...section, options });

      if (isDefined(subsectionParamKey)) {
        onDeselectSection(subsectionParamKey, updatedSections);
      }

      onChange(updatedSections);
    }
  };

  // remove all options from a section
  const onDeselectSection = (
    paramKey: MediaFilterParamKey,
    updatedSections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => {
    const section = sections.get(paramKey);

    if (isDefined(section)) {
      const options = section.options.map((option) => ({
        ...option,
        isSelected: false,
      }));

      // update subsection
      updatedSections.set(section.paramKey, { ...section, options });
    }
  };

  const isDistributed = sections
    .get('isDistributed')
    ?.options.find((option) => option.isSelected)?.value;

  // TODO: PD-264130 - Remove content_library_labels_improvements feature flag
  const filteredParentSections = isContentLibraryLabelsImprovementsEnabled
    ? filteredSections.filter(
        (section) => !SUBSECTION_PARAM_KEYS.includes(section.paramKey),
      )
    : filteredSections;

  const productIdsSection: FilterSection<string> =
    getSection(sections, 'productIds') ??
    DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION;

  const { data: allProductDetails } = useProductDetailsQuery({
    fetchAllVariants: true,
    productStyleIds: productIdsSection.options
      .filter((option) => option.isSelected)
      .map((option) => option.value),
  });

  return (
    <>
      {filtersCount > 0 && (
        <>
          <CollapsibleSection
            isCollapsed={true}
            rightComponent={<Count>{filtersCount}</Count>}
            title='Filtered By'
          >
            <Sections>
              {filteredParentSections.map((section) => {
                const title =
                  section.paramKey === 'distribution'
                    ? getDistributionTitle(!!isDistributed)
                    : FilterSectionTitle[section.paramKey];

                // TODO: PD-264130 - Remove content_library_labels_improvements feature flag
                if (
                  isContentLibraryLabelsImprovementsEnabled &&
                  section.paramKey === 'hasLabels'
                ) {
                  const subsection = filteredSections.find(
                    ({ paramKey }) => paramKey === 'labels',
                  );

                  return (
                    <SelectedOptions
                      key={section.paramKey}
                      isDisabled={disabledFilters?.includes(section.paramKey)}
                      onDeselect={onDeselect}
                      section={section}
                      subsection={subsection}
                      title={title}
                    />
                  );
                }

                // TODO: PD-297467 - Remove content_library_products_improvements feature flag
                if (
                  isContentLibraryProductsImprovementsEnabled &&
                  section.paramKey === 'hasProducts' &&
                  allProductDetails
                ) {
                  const subsection = filteredSections.find(
                    ({ paramKey }) => paramKey === 'productIds',
                  );

                  return (
                    <SelectedOptions
                      key={section.paramKey}
                      isDisabled={disabledFilters?.includes(section.paramKey)}
                      onDeselect={onDeselect}
                      section={section}
                      subsection={
                        subsection && {
                          ...subsection,
                          options: subsection.options.map((option) => ({
                            ...option,
                            label:
                              allProductDetails.productVariants.find(
                                (product) => product.styleId === option.value,
                              )?.title ?? option.label,
                          })),
                        }
                      }
                      title={title}
                    />
                  );
                }

                return (
                  <SelectedOptions
                    key={section.paramKey}
                    isDisabled={disabledFilters?.includes(section.paramKey)}
                    onDeselect={onDeselect}
                    section={section}
                    title={title}
                  />
                );
              })}
            </Sections>
            <BodyText>
              <DefaultButton onClick={onReset}>Clear All Filters</DefaultButton>
            </BodyText>
          </CollapsibleSection>
          <HR />
        </>
      )}
    </>
  );
};

const Sections = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const Count = styled.span(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${theme.spacings[5]};
    height: ${theme.spacings[5]};
    border-radius: 50%;
    background: ${theme.colors.gray0};
  `,
);
